import './styles.scss';

import { Button } from '@components/Button/Button';
import { FormItem, FormItemRule, IFormItem } from '@components/Form/FormItem/FormItem';
import {
  formFinishValues,
  formInitialValues,
  formSchemaFlatten,
  isGroupNode,
} from '@components/Form/FormUtils';
import { Col, Form as AntForm, FormInstance, FormProps, Row } from 'antd';
import { useForceUpdate } from 'antd/es/_util/hooks/useForceUpdate';
import { ButtonType } from 'antd/es/button';
import _ from 'lodash';
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

export interface IFormGroupItem {
  type: 'group';
  key: string;
  label?: string;
  required?: boolean;
  separator?: string | string[];
  split?: boolean;
  items: IFormItem[];
  validation?: FormItemRule[] | ((form: FormInstance) => FormItemRule[]);
}

export type IFormNode = IFormItem | IFormGroupItem;

export interface IFormProps extends Omit<FormProps, 'layout'> {
  viewMode?: boolean;
  schema: IFormNode[];
  data?: Record<string, any>;
  onSubmit?: (values: any) => void;
  onCancel?: () => void;
  layout?: {
    formLayout: 'horizontal' | 'vertical';
    itemLayout: 'horizontal' | 'vertical';
  };
  buttons?: {
    submit?: {
      text?: string;
      type?: ButtonType;
    };
    cancel?: {
      text?: string;
      type?: ButtonType;
    };
    loading?: boolean;
  };
  buttonsAlign?: 'start' | 'center' | 'end';
  buttonsDirection?: 'horizontal' | 'vertical';
  buttonsGap?: number;
}

const mergeValidation = (
  childValidation?: FormItemRule[] | ((form: FormInstance) => FormItemRule[]),
  groupValidation?: FormItemRule[] | ((form: FormInstance) => FormItemRule[]),
): FormItemRule[] | ((form: FormInstance) => FormItemRule[]) | undefined => {
  if (!groupValidation) return childValidation;

  return (form: FormInstance) => {
    const childRules = _.isFunction(childValidation)
      ? childValidation(form)
      : childValidation;
    const groupRules = _.isFunction(groupValidation)
      ? groupValidation(form)
      : groupValidation;

    return [...(childRules ?? []), ...(groupRules ?? [])];
  };
};

export const Form = forwardRef<FormInstance, IFormProps>(
  (
    {
      viewMode = false,
      schema,
      data,
      onSubmit,
      onCancel,
      buttons,
      layout = {
        formLayout: 'vertical',
        itemLayout: 'horizontal',
      },
      ...formProps
    },
    ref,
  ) => {
    const [form] = AntForm.useForm();
    const [, forceUpdate] = useForceUpdate();
    const [loading, setLoading] = useState(false);
    const isInitialized = useRef(false);
    const flatSchema = formSchemaFlatten(schema);
    useImperativeHandle(ref, () => form, [form]);

    useEffect(() => {
      if (!_.isEmpty(data) && !_.isEmpty(flatSchema) && !isInitialized.current) {
        form.setFieldsValue(formInitialValues(data, flatSchema));
        isInitialized.current = true;
        forceUpdate();
      }
    }, [schema, data]);

    const onFinish = async (values: any) => {
      setLoading(true);

      try {
        await onSubmit?.(formFinishValues(values, flatSchema));
      } finally {
        setLoading(false);
      }
    };

    const groupedItems: IFormNode[][] = schema
      .reduce(
        (acc, node) => {
          acc[acc.length - 1].push(node);
          if ((isGroupNode(node) && node.split) || (!isGroupNode(node) && node.split)) {
            acc.push([]);
          }

          return acc;
        },
        [[]] as IFormNode[][],
      )
      .filter((column) => !_.isEmpty(column));

    const renderGroup = (group: IFormGroupItem) => (
      <AntForm.Item
        key={group.key}
        label={group.label ? <b>{`${group.label}:`}</b> : null}
        required={group.required}
        className="form-inline-group-item">
        <div className="form-inline-group">
          {group.items.map((child, idx) => {
            const separator = Array.isArray(group.separator)
              ? group.separator[idx]
              : group.separator;

            return (
              <React.Fragment key={child.key}>
                <FormItem
                  viewMode={viewMode}
                  field={{
                    ...child,
                    hideLabel: true,
                    noStyle: true,
                    validation: mergeValidation(child.validation, group.validation),
                  }}
                />
                {idx < group.items.length - 1 && (
                  <span className="form-inline-separator">{separator ?? '—'}</span>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </AntForm.Item>
    );

    const renderNode = (node: IFormNode) => {
      if (isGroupNode(node)) return renderGroup(node);

      return <FormItem viewMode={viewMode} field={node} />;
    };

    const renderVerticalForm = () => (
      <Row gutter={25} align={'top'}>
        {groupedItems.map((group, index) => (
          <Col key={index} flex={`${100 / groupedItems.length}%`}>
            {group.map((item) => (
              <React.Fragment key={item.key}>{renderNode(item)}</React.Fragment>
            ))}
          </Col>
        ))}
      </Row>
    );

    const renderHorizontalForm = () => (
      <div>
        {groupedItems.map((group, index) => (
          <Row gutter={25} key={index}>
            {group.map((item) => (
              <Col key={item.key} flex={'20%'}>
                {renderNode(item)}
              </Col>
            ))}
          </Row>
        ))}
      </div>
    );

    const renderFormButtons = () => {
      return !viewMode ? (
        <div className="form-footer">
          <Button
            type={buttons?.submit?.type ?? 'primary'}
            className="form-button"
            htmlType="submit"
            loading={loading}
            text={buttons?.submit?.text ?? 'Сохранить'}
          />
          <Button
            type={buttons?.cancel?.type ?? 'default'}
            className="form-button"
            onClick={() => {
              form?.resetFields();
              onCancel?.();
            }}
            htmlType="reset"
            disabled={loading}
            text={buttons?.cancel?.text ?? 'Отменить'}
          />
        </div>
      ) : null;
    };

    return (
      <AntForm
        requiredMark={false}
        form={form}
        disabled={viewMode}
        onValuesChange={forceUpdate}
        onFinish={onFinish}
        layout={layout.itemLayout}
        labelCol={layout.itemLayout == 'horizontal' ? { span: 10 } : undefined}
        wrapperCol={layout.itemLayout == 'horizontal' ? { span: 14 } : undefined}
        labelAlign={'left'}
        labelWrap
        colon={false}
        {...formProps}>
        {layout.formLayout == 'vertical' ? renderVerticalForm() : renderHorizontalForm()}
        {renderFormButtons()}
      </AntForm>
    );
  },
);
